import { Component } from '@theme/component';

/**
 * @typedef {Object} FoodPairingSearchItem
 * @property {string} name
 * @property {string} searchText
 * @property {string} url
 * @property {string} [category]
 */

/**
 * @typedef {Object} FoodPairingIndexRefs
 * @property {HTMLElement} tablist - Tab list container
 * @property {HTMLButtonElement[]} tabs - Category tab buttons
 * @property {HTMLElement[]} tabPanels - Category tab panels
 */

/**
 * Wine & food pairing index: category tabs with hero typeahead search.
 * Search input is in a separate block (food-pairing-search) connected by ID.
 *
 * @extends {Component<FoodPairingIndexRefs>}
 */
class FoodPairingIndex extends Component {
  /** @type {number} */
  #activeTabIndex = 0;

  /** @type {number|undefined} */
  #debounceTimer;

  /** @type {HTMLInputElement|null} */
  #searchInput = null;

  /** @type {HTMLElement|null} */
  #searchResults = null;

  /** @type {number} Active typeahead option index, or -1 for none */
  #typeaheadActiveIndex = -1;

  /** @type {FoodPairingSearchItem[]} */
  #pairings = [];

  connectedCallback() {
    super.connectedCallback();

    const dataEl = this.querySelector('script[type="application/json"][data-food-pairing-search-index]');
    if (dataEl?.textContent) {
      try {
        const parsed = JSON.parse(dataEl.textContent.trim());
        this.#pairings = Array.isArray(parsed) ? /** @type {FoodPairingSearchItem[]} */ (parsed) : [];
      } catch {
        this.#pairings = [];
      }
    }

    this.#searchInput = document.getElementById('food-pairing-search-input');
    this.#searchResults = document.getElementById('food-pairing-search-results');

    if (this.#searchInput) {
      this.#searchInput.addEventListener('input', this.#handleSearchInput);
      this.#searchInput.addEventListener('keydown', this.#handleSearchKeydown);
      if (this.#searchResults?.id) {
        this.#searchInput.setAttribute('role', 'combobox');
        this.#searchInput.setAttribute('aria-autocomplete', 'list');
        this.#searchInput.setAttribute('aria-controls', this.#searchResults.id);
        this.#searchInput.setAttribute('aria-expanded', 'false');
      }
    }

    document.addEventListener('click', this.#handleDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#searchInput) {
      this.#searchInput.removeEventListener('input', this.#handleSearchInput);
      this.#searchInput.removeEventListener('keydown', this.#handleSearchKeydown);
      this.#searchInput.removeAttribute('aria-activedescendant');
      this.#searchInput.removeAttribute('aria-expanded');
      this.#searchInput.removeAttribute('aria-controls');
      this.#searchInput.removeAttribute('aria-autocomplete');
      this.#searchInput.removeAttribute('role');
    }

    document.removeEventListener('click', this.#handleDocumentClick);
    clearTimeout(this.#debounceTimer);
  }

  /** @returns {HTMLButtonElement[]} */
  #resolveTabs() {
    const r = this.refs.tabs;
    if (Array.isArray(r) && r.length > 0) return /** @type {HTMLButtonElement[]} */ (r);
    return Array.from(this.querySelectorAll('.food-pairing-index__tab'));
  }

  /** @returns {HTMLElement[]} */
  #resolvePanels() {
    const r = this.refs.tabPanels;
    if (Array.isArray(r) && r.length > 0) return r;
    return Array.from(this.querySelectorAll('.food-pairing-index__panels > [role="tabpanel"]'));
  }

  /**
   * Tab click handler — resolves closest button via delegation.
   * @param {Event} event
   */
  handleTabClick(event) {
    event.preventDefault();
    const el =
      event.target instanceof Element
        ? event.target
        : event.target?.parentNode instanceof Element
          ? event.target.parentNode
          : null;
    const tab = el?.closest?.('button[data-category-index]');
    if (!(tab instanceof HTMLButtonElement)) return;

    const index = Number.parseInt(tab.dataset.categoryIndex ?? '', 10);
    if (Number.isNaN(index)) return;

    this.#clearSearch();
    this.#activateTab(index);
  }

  /** @param {KeyboardEvent} event */
  handleTabKeydown(event) {
    const tabs = this.#resolveTabs();
    if (!tabs.length) return;

    const currentEl = event.target instanceof Element ? event.target : null;
    const currentTab = currentEl?.closest?.('button[data-category-index]');
    const currentIndex =
      currentTab instanceof HTMLButtonElement ? tabs.indexOf(currentTab) : -1;
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      event.preventDefault();
      nextIndex = 0;
    } else if (event.key === 'End') {
      event.preventDefault();
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    this.#clearSearch();
    this.#activateTab(nextIndex);
    tabs[nextIndex].focus();
  }

  /** @param {Event} event */
  #handleSearchInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      this.#performSearch(input.value.trim());
    }, 150);
  };

  /** @returns {HTMLLIElement[]} */
  #getTypeaheadOptionLis() {
    const resultsEl = this.#searchResults;
    if (!resultsEl) return [];
    return Array.from(resultsEl.querySelectorAll(':scope > li[role="option"]'));
  }

  /** @param {number} index */
  #setTypeaheadActiveIndex(index) {
    const input = this.#searchInput;
    const options = this.#getTypeaheadOptionLis();
    if (!input || !options.length) {
      this.#typeaheadActiveIndex = -1;
      input?.removeAttribute('aria-activedescendant');
      return;
    }

    const clamped =
      index < -1 ? -1 : index >= options.length ? options.length - 1 : index;
    this.#typeaheadActiveIndex = clamped;

    for (const [i, li] of options.entries()) {
      li.setAttribute('aria-selected', i === clamped ? 'true' : 'false');
    }

    if (clamped >= 0) {
      const active = options[clamped];
      if (active) {
        input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      }
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  }

  #syncTypeaheadExpanded() {
    const input = this.#searchInput;
    const resultsEl = this.#searchResults;
    if (!input || !resultsEl) return;
    const open = !resultsEl.hidden && this.#getTypeaheadOptionLis().length > 0;
    input.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /** @param {KeyboardEvent} event */
  #handleSearchKeydown = (event) => {
    if (!(event instanceof KeyboardEvent) || !this.#searchInput || !this.#searchResults) return;

    const resultsEl = this.#searchResults;
    let options = this.#getTypeaheadOptionLis();
    if (!options.length) return;

    const key = event.key;

    if (key === 'ArrowDown') {
      event.preventDefault();
      if (resultsEl.hidden) {
        resultsEl.hidden = false;
        options = this.#getTypeaheadOptionLis();
        if (!options.length) return;
      }
      const next =
        this.#typeaheadActiveIndex < options.length - 1
          ? this.#typeaheadActiveIndex + 1
          : this.#typeaheadActiveIndex;
      this.#setTypeaheadActiveIndex(next);
      this.#syncTypeaheadExpanded();
      return;
    }

    if (key === 'ArrowUp') {
      event.preventDefault();
      if (resultsEl.hidden) return;
      const next = this.#typeaheadActiveIndex > -1 ? this.#typeaheadActiveIndex - 1 : -1;
      this.#setTypeaheadActiveIndex(next);
      this.#syncTypeaheadExpanded();
      return;
    }

    if (key === 'Enter') {
      if (this.#typeaheadActiveIndex >= 0 && !resultsEl.hidden) {
        event.preventDefault();
        const li = options[this.#typeaheadActiveIndex];
        const link = li?.querySelector('a.food-pairing-search__result');
        if (link instanceof HTMLAnchorElement) {
          link.click();
        }
      }
      return;
    }

    if (key === 'Escape') {
      if (!resultsEl.hidden || this.#typeaheadActiveIndex >= 0) {
        event.preventDefault();
      }
      this.#setTypeaheadActiveIndex(-1);
      resultsEl.hidden = true;
      this.#syncTypeaheadExpanded();
      return;
    }

    if (key === 'Home' && !resultsEl.hidden && this.#typeaheadActiveIndex >= 0) {
      event.preventDefault();
      this.#setTypeaheadActiveIndex(0);
      return;
    }

    if (key === 'End' && !resultsEl.hidden && this.#typeaheadActiveIndex >= 0) {
      event.preventDefault();
      this.#setTypeaheadActiveIndex(options.length - 1);
      return;
    }
  };

  /** @param {number} index */
  #activateTab(index) {
    const tabs = this.#resolveTabs();
    const panels = this.#resolvePanels();
    if (!tabs.length || !panels.length) return;
    if (index < 0 || index >= tabs.length || index >= panels.length) return;

    for (const [i, tab] of tabs.entries()) {
      const isActive = i === index;
      tab.classList.toggle('food-pairing-index__tab--active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of panels.entries()) {
      panel.hidden = i !== index;
    }

    this.#activeTabIndex = index;
  }

  /** @param {string} query */
  #performSearch(query) {
    if (!query || query.length < 1) {
      this.#hideSearchResults();
      return;
    }

    const normalizedQuery = query.toLowerCase();
    const matches = [];
    for (const p of this.#pairings) {
      const text = typeof p.searchText === 'string' ? p.searchText : '';
      if (text.includes(normalizedQuery)) {
        matches.push(p);
      }
    }

    this.#updateTypeahead(matches, normalizedQuery);
  }

  /**
   * @param {FoodPairingSearchItem[]} matches
   * @param {string} query
   */
  #updateTypeahead(matches, query) {
    const resultsEl = this.#searchResults;
    if (!resultsEl) return;

    resultsEl.innerHTML = '';

    if (!query || matches.length === 0) {
      resultsEl.hidden = true;
      this.#typeaheadActiveIndex = -1;
      if (this.#searchInput) {
        this.#searchInput.removeAttribute('aria-activedescendant');
        this.#searchInput.setAttribute('aria-expanded', 'false');
      }
      return;
    }

    const maxResults = 8;
    const displayed = matches.slice(0, maxResults);

    for (const [i, p] of displayed.entries()) {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.id = `food-pairing-typeahead-opt-${i}`;

      const link = document.createElement('a');
      link.href = typeof p.url === 'string' && p.url ? p.url : '#';
      link.className = 'food-pairing-search__result';

      const nameSpan = document.createElement('span');
      nameSpan.textContent = typeof p.name === 'string' ? p.name : '';

      link.appendChild(nameSpan);

      if (p.category && String(p.category).trim()) {
        const catSpan = document.createElement('span');
        catSpan.className = 'food-pairing-search__result-category';
        catSpan.textContent = String(p.category).trim();
        link.appendChild(catSpan);
      }

      li.appendChild(link);
      resultsEl.appendChild(li);
    }

    if (matches.length > maxResults) {
      const moreEl = document.createElement('li');
      moreEl.className = 'food-pairing-search__result-more';
      moreEl.setAttribute('role', 'option');
      moreEl.setAttribute('aria-selected', 'false');
      moreEl.id = `food-pairing-typeahead-opt-${displayed.length}`;
      moreEl.textContent = `+ ${matches.length - maxResults} meer`;
      resultsEl.appendChild(moreEl);
    }

    this.#typeaheadActiveIndex = -1;
    if (this.#searchInput) {
      this.#searchInput.removeAttribute('aria-activedescendant');
    }

    resultsEl.hidden = false;
    this.#syncTypeaheadExpanded();
  }

  #hideSearchResults() {
    if (this.#searchResults) {
      this.#searchResults.hidden = true;
      this.#searchResults.innerHTML = '';
    }
    this.#typeaheadActiveIndex = -1;
    if (this.#searchInput) {
      this.#searchInput.removeAttribute('aria-activedescendant');
      this.#searchInput.setAttribute('aria-expanded', 'false');
    }
  }

  #clearSearch() {
    if (this.#searchInput) {
      this.#searchInput.value = '';
    }
    this.#hideSearchResults();
  }

  /** @param {Event} event */
  #handleDocumentClick = (event) => {
    if (!this.#searchResults) return;
    const searchContainer = this.#searchInput?.closest('.food-pairing-search');
    const clickedInSearch = searchContainer && event.target instanceof Node && searchContainer.contains(event.target);
    const clickedInIndex = event.target instanceof Node && this.contains(event.target);
    if (!clickedInSearch && !clickedInIndex) {
      this.#hideSearchResults();
    }
  };
}

if (!customElements.get('food-pairing-index')) {
  customElements.define('food-pairing-index', FoodPairingIndex);
}
